import React, { useState, useCallback } from 'react';
import { Link, Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import CardList from '../components/cards/card-list';
import { useChangePermissionsMutation, useGetGroupQuery, useGetGroupMembersQuery } from '../services/groups.service';
import { MembersBehavior } from '../models/group';
import ConfirmModal from '../components/modals/confirm-modal';
import Search from '../components/search';
import linkTo from '../utils/linkTo';
import { searchSortHeaderStyles } from './__fixtures__';
import { listResourcesFallbackUIProps } from './group-fallback-ui';
import { OptionItem, EventData } from '../components/cards/card.types';

export interface Props {
  groupId: string;
}

const membersBehavior = new MembersBehavior();

export default function GroupMembers({ groupId }: Props) {
  const [params, setParams] = useState({
    id: groupId,
    page: 1,
    per_page: 100,
    sort: 'user',
    search: '',
  });

  const { data: group, isFetching, isError } = useGetGroupMembersQuery({ ...params });
  const { data: groupDetails } = useGetGroupQuery({ id: params.id });
  const [memberId, setMemberId] = useState('');
  const [changePermission] = useChangePermissionsMutation();

  const groupMembers = group?.memberships ?? [];

  const showSearch = !!groupMembers.length || (!groupMembers.length && !!params.search.length);
  const isSearching = params.search !== '';
  const [removedMemberName, setRemovedMemberName] = useState('');

  const handleMemberRemove = () => {
    changePermission({
      type: 'group_members',
      groupId,
      remove: [memberId],
      item: removedMemberName,
    });
    resetDeleteModal();
  };

  const handleFetchMore = useCallback(() => {
    if (group && (params.page !== group.total_pages)) {
      setParams({ ...params, page: params.page + 1 });
    }
  }, [group, params]);

  const handleSearch = (search: string) => {
    setParams({ ...params, page: 1, search });
  };
  const removeClick = (id: string, title: string) => {
    setMemberId(id);
    setRemovedMemberName(title);
  };
  const resetDeleteModal = () => setMemberId('');

  const { EmptyImage, emptyText, emptyTextHelper } = listResourcesFallbackUIProps({
    resource: 'members',
    isSearching,
  });

  const onRemoveEvent = (_dataType: OptionItem, eventData: EventData) => {
    removeClick(eventData.id, eventData.title || '');
  };

  const actionItems: OptionItem[] = [];
  if (!groupDetails?.isManaged) actionItems.push(OptionItem.Delete);

  return (
    <>
      <ConfirmModal
        onPositive={handleMemberRemove}
        onNegative={resetDeleteModal}
        positive="Remove"
        title="Remove Member?"
        open={!!memberId}
        maxWidth="xs"
      >
        <Typography>{membersBehavior.getDeleteText()}</Typography>
        <Link target="_blank" href={linkTo.groups.removingAccess} rel="noreferrer">
          Learn more
        </Link>
      </ConfirmModal>
      {showSearch && (
        <Stack sx={{ ...searchSortHeaderStyles }}>
          <Search
            setGlobalFilter={handleSearch}
            placeholder="Search name, email, or role"
          />
        </Stack>
      )}
      <CardList
        rows={groupMembers}
        onEvent={onRemoveEvent}
        onFetchMore={handleFetchMore}
        isLoading={isFetching}
        error={isError && 'There was an error fetching members.'}
        EmptyImage={EmptyImage}
        emptyText={emptyText}
        emptyTextHelper={emptyTextHelper}
        actionItems={actionItems}
      />
    </>
  );
}
